import { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { useContent } from "@/hooks/useContent";

const inputCls =
  "w-full bg-[#08090C] border border-[#232638] focus:border-amber-400/70 outline-none px-4 py-3.5 text-sm text-zinc-200 placeholder:text-zinc-600 transition-colors duration-300";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2";

const ContactSection = () => {
  const { data: site } = useContent("/content/site.json");
  const [form, setForm] = useState({ name: "", email: "", date: "", city: "", type: "Baile Charme", message: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    const email = site?.contactEmail || "contato@ronaldodj.com";
    const subject = `Contato pelo site — ${form.name}`;
    const body = [
      `Nome: ${form.name}`,
      `E-mail: ${form.email}`,
      `Data do evento: ${form.date || "a definir"}`,
      `Cidade/UF: ${form.city || "a definir"}`,
      `Tipo de evento: ${form.type}`,
      "",
      form.message,
    ].join("\n");
    window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    toast.success("Abrimos seu aplicativo de e-mail com a mensagem pronta.", {
      description: "Envio direto pelo site (Formspree) será ativado em breve.",
    });
  };

  return (
    <section id="contato" data-testid="contact-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
      <SectionHeading
        chapter="06 // Orçamento & contato"
        title="Entre em contato ou apenas diga olá"
        note="Contratação de shows, parcerias e recados para a rádio."
      />
      <div className="grid grid-cols-12 gap-8 lg:gap-14">
        <div className="col-span-12 lg:col-span-5">
          <Reveal>
            <p className="text-sm sm:text-base text-zinc-300 leading-relaxed font-light max-w-md">
              Quer levar o som das pistas para o seu evento? Manda os detalhes — data, cidade e tipo de festa — que a
              resposta vem rápido.
            </p>
            <p className="mt-8 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">E-mail direto</p>
            <a
              href={`mailto:${site?.contactEmail || "contato@ronaldodj.com"}`}
              data-testid="contact-email-link"
              className="font-display text-xl sm:text-2xl font-bold tracking-tight text-amber-400 hover:text-amber-300 transition-colors"
            >
              {site?.contactEmail || "contato@ronaldodj.com"}
            </a>
            <p className="mt-8 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Redes</p>
            <div className="flex flex-wrap gap-2">
              {(site?.socials || []).map((s) => (
                <a
                  key={s.label}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`contact-social-${s.label.toLowerCase()}`}
                  className="font-mono text-[10px] uppercase tracking-[0.2em] border border-[#3F4462] hover:border-amber-400/60 hover:text-amber-400 text-zinc-400 px-3.5 py-2 rounded-full transition-colors duration-300"
                >
                  {s.label}
                </a>
              ))}
            </div>
          </Reveal>
        </div>
        <div className="col-span-12 lg:col-span-7">
          <Reveal delay={0.1}>
            <form onSubmit={onSubmit} data-testid="contact-form" className="border border-[#232638] bg-[#10121A] p-6 sm:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls} htmlFor="cf-name">Nome</label>
                  <input id="cf-name" data-testid="contact-form-name-input" required value={form.name} onChange={set("name")} placeholder="Seu nome" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="cf-email">E-mail</label>
                  <input id="cf-email" type="email" data-testid="contact-form-email-input" required value={form.email} onChange={set("email")} placeholder="voce@email.com" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="cf-date">Data do evento</label>
                  <input id="cf-date" type="date" data-testid="contact-form-event-date-input" value={form.date} onChange={set("date")} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="cf-city">Cidade / UF</label>
                  <input id="cf-city" data-testid="contact-form-city-input" value={form.city} onChange={set("city")} placeholder="Rio de Janeiro — RJ" className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="cf-type">Tipo de evento</label>
                  <select id="cf-type" data-testid="contact-form-event-type-select" value={form.type} onChange={set("type")} className={inputCls}>
                    <option>Baile Charme</option>
                    <option>Festa Funk 90s</option>
                    <option>Noite Freestyle</option>
                    <option>Club Gig</option>
                    <option>Evento particular</option>
                    <option>Outro assunto</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="cf-message">Mensagem</label>
                  <textarea id="cf-message" data-testid="contact-form-message-input" required rows={5} value={form.message} onChange={set("message")} placeholder="Conta os detalhes do evento..." className={`${inputCls} resize-none`} />
                </div>
              </div>
              <button
                type="submit"
                data-testid="contact-form-submit-button"
                className="mt-7 inline-flex items-center gap-3 bg-amber-400 text-[#08090C] font-mono text-xs uppercase tracking-[0.2em] font-bold px-8 py-4 rounded-full hover:bg-amber-300 transition-colors duration-300"
              >
                <Send size={15} /> Enviar mensagem
              </button>
              <p className="mt-4 font-mono text-[10px] text-zinc-600 tracking-wide">
                O envio abre seu aplicativo de e-mail com tudo preenchido. Envio direto pelo site chega em breve.
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
